import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import PropTypes from 'prop-types';

import { useViewTracking } from 'hooks';

// Components
import { Mui } from 'components';
import { Stack } from 'components/layouts';

const useStyles = Mui.makeStyles(theme => ({
  imageWrapper: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%', // 16:9 Aspect Ratio (9 / 16 * 100%)
    overflow: 'hidden',
  },
  roundedAll: { borderRadius: theme.shape.borderRadius },
  roundedTop: {
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  clickableImage: {
    cursor: 'pointer',
    padding: 0,
    border: 'none',
    background: 'none',
    outline: 'none',
  },
  indicator: {
    color: theme.palette.primary.contrastText,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  content: { whiteSpace: 'pre-line' },
}));

function PopupItem({ type, slug, popup, onInteraction, totalPopups, currentIndex }) {
  // Constants
  const { uid, title, content, image, ctaText, ctaHref, hasClicked } = popup || {};

  // Hooks
  const classes = useStyles();
  const history = useHistory();
  const elementRef = useViewTracking(popup?.hasViewed, () => onInteraction?.(uid, 'view'), {
    threshold: 0.8,
    delay: 2000,
  });

  // Constants
  const hasTitle = !!title && typeof title === 'string' && title.trim() !== '';
  const hasContent = !!content && typeof content === 'string' && content.trim() !== '';
  const hasCta = !!ctaText && typeof ctaText === 'string' && ctaText.trim() !== '' && !!ctaHref;

  const shouldShowTitle = hasTitle && hasContent;
  const isImageOnly = !!image && !shouldShowTitle && !hasContent && !hasCta;

  // Actions
  const handleClick = useCallback(
    url => {
      if (!hasClicked) {
        onInteraction?.(uid, 'click');
      }
      const hasUrl = !!url && typeof url === 'string';
      if (hasUrl && url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      if (hasUrl && url.startsWith('/')) {
        history.push(`/${type}/${slug}${url}`);
      }
    },
    [uid, onInteraction, type, slug, history, hasClicked],
  );

  // Render
  const renderImage = imgSrc => {
    const imageElement = (
      <img src={imgSrc} alt={title || 'Imagem do pop-up'} className={classes.image} />
    );

    if (ctaHref) {
      return (
        <button
          type="button"
          onClick={() => handleClick(ctaHref)}
          className={`${classes.image} ${classes.clickableImage}`}>
          {imageElement}
        </button>
      );
    }
    return imageElement;
  };

  const renderIndicator = () => {
    if (totalPopups <= 1) return null;
    return (
      <Mui.Box position="absolute" bottom={16} right={16} zIndex={10}>
        <Mui.Chip label={`${currentIndex + 1} / ${totalPopups}`} className={classes.indicator} />
      </Mui.Box>
    );
  };

  if (isImageOnly) {
    return (
      <Mui.Box ref={elementRef} className={`${classes.imageWrapper} ${classes.roundedAll}`}>
        {renderImage(image)}
        {renderIndicator()}
      </Mui.Box>
    );
  }

  return (
    <Mui.Box ref={elementRef} style={{ position: 'relative' }}>
      {!!image && (
        <Mui.Box className={`${classes.imageWrapper} ${classes.roundedTop}`}>
          {renderImage(image)}
          {renderIndicator()}
        </Mui.Box>
      )}
      {(shouldShowTitle || hasContent) && (
        <Mui.Box p={3}>
          <Stack>
            {shouldShowTitle && <Mui.Typography variant="h4">{title}</Mui.Typography>}
            {hasContent && (
              <Mui.Typography variant="body1" color="textSecondary" className={classes.content}>
                {content}
              </Mui.Typography>
            )}
          </Stack>
        </Mui.Box>
      )}
      {hasCta && (
        <Mui.Box p={2} pt={0}>
          <Mui.Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={() => handleClick(ctaHref)}
            endIcon={<Mui.Icon>arrow_forward</Mui.Icon>}>
            {ctaText}
          </Mui.Button>
        </Mui.Box>
      )}
    </Mui.Box>
  );
}

PopupItem.propTypes = {
  type: PropTypes.oneOf(['sellers', 'institutions']).isRequired,
  slug: PropTypes.string,
  popup: PropTypes.object.isRequired,
  onInteraction: PropTypes.func.isRequired,
  totalPopups: PropTypes.number,
  currentIndex: PropTypes.number,
};

export default PopupItem;
